import { IField, spLogTags, structuredCloneL } from '@eventr/common'
import {
    ColoursObj,
    Gutters,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
    useValidatorContext,
} from '@eventr/common-storybook'
import { Divider, Stack } from '@mui/material'
import { useSignal } from '@preact/signals-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { IEditComponent, ISubPage } from '../../../data/Data'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { NavigationView } from '../../layout/NavigationView'
import { ToolbarItemProps } from '../../layout/TopToolbar'
import { ComponentFinderEdit } from '../componentFinderEdit'
import { useEditSectionContext } from '../editSectionProvider'

interface IEditSubItemProps {
    id?: string
    subPage: ISubPage
    _id?: string
}
export const EditSubItem = ({ id, subPage }: IEditSubItemProps) => {
    const { section, getValue, updateValues, getItinTitle, isNewItem } =
        useEditSectionContext()
    const { getFieldById, getFieldsForSection } = useBoundStore(
        useShallow((s) => {
            return {
                getFieldById: s.getFieldById,
                getFieldsForSection: s.getFieldsForSection,
            }
        })
    )
    const { isAllValid, setShowErrors, setValidationOn } = useValidatorContext()
    const { idSubItem, idItem } = useParams<{
        idSubItem: string
        idItem: string
    }>()
    const ogValues = useSignal({})
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    useEffect(() => {
        const field = getFieldById(subPage.fieldId)
        const fieldValue = getValue(field, idItem)
        const bookingValue = fieldValue?.find((item: any) => {
            return item._id === idSubItem
        })

        ogValues.value = structuredCloneL(bookingValue)
    }, [])

    const [field, fieldValue] = useMemo(() => {
        const field = getFieldById(subPage.fieldId)
        const fieldValue = getValue(field, idItem)
        return [field, fieldValue]
    }, [getValue, getFieldById])

    const headerText = useMemo(() => {
        if (isNewItem(idSubItem)) {
            return 'Add Booking'
        }
        return 'Edit Booking'
    }, [isNewItem, idSubItem])

    const subHeaderText = () => {
        const headerKey =
            section?.properties?.mobileEditFieldOptions?.headerKeyValue
        if (!headerKey) {
            return getItinTitle()
        }

        const field = getFieldsForSection(section.id)?.find(
            (field: IField) =>
                Number.parseInt(field.id.toString()) === headerKey
        )
        if (!field) {
            return getItinTitle()
        }
        const keyFieldValue = getValue(field, idItem)

        return keyFieldValue
    }

    // removes the element if its empty and the user goes back
    const removeElement = () => {
        const newFieldValue = fieldValue.filter((item: any) => {
            return item._id !== idSubItem
        })
        if (Object.keys(ogValues.value).length > 1) {
            newFieldValue.push(ogValues.value)
        }

        updateValues({
            value: newFieldValue,
            field: field,
            _id: idItem,
        })
    }

    const onBackPressed = () => {
        if (allowSave) {
            replaceDialog({
                node: (
                    <MobileConfirmDialog
                        text="You have unsaved changes. Are you sure you want to leave this page?"
                        header="Unsaved Changes"
                        onConfirm={() => {
                            removeElement()
                            clearDialog()
                            setValidationOn(false)
                            window.goBack()
                        }}
                        onCancel={() => {
                            clearDialog()
                        }}
                    />
                ),
                backdropClosable: false,
            })
        } else {
            removeElement()
            setValidationOn(false)
            window.goBack()
        }
    }

    function openDeleteDialog() {
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text="Are you sure you want to remove this Item ?"
                    header="Delete Item"
                    onCancel={() => {
                        clearDialog()
                    }}
                    onConfirm={onDelete}
                />
            ),
            backdropClosable: false,
        })
    }

    const deleteButtonText = () => {
        if (subPage.deleteButtonText) {
            return subPage.deleteButtonText
        } else {
            return 'Delete Item'
        }
    }
    const componentsToRender = () => {
        if (subPage.components) {
            return subPage.components
        } else {
            return []
        }
    }

    const onDelete = async () => {
        if (subPage.fieldId) {
            const newFieldValue = fieldValue.filter((item: any) => {
                return item._id !== idSubItem
            })

            updateValues({ value: newFieldValue, field: field, _id: idItem })
        }
        clearDialog()
        window.goBack()
    }
    const allowSave = useMemo(() => {
        const bookingValue = fieldValue?.find((item: any) => {
            return item._id === idSubItem
        })
        return JSON.stringify(ogValues.value) !== JSON.stringify(bookingValue)
    }, [ogValues.value, getValue, getFieldById])

    const navigationBarTrailing = () => {
        const popoverItems = [] as ToolbarItemProps[]
        popoverItems.push({
            id: 'saveButton',
            buttonText: i18n.t('general.done'),
            colour: ColoursObj.core.primary,
            useButton: true,
            action: () => {
                if (!isAllValid) {
                    spLogTags('Some fields are not valid', ['SAVE'])
                    setShowErrors(true)
                    return
                }
                if (allowSave && isAllValid) {
                    setShowErrors(false)
                    window.goBack()
                } else {
                    /// Nothing to save so go back.
                    window.goBack()
                }
            },
        })

        return popoverItems
    }
    const backButton: ToolbarItemProps = {
        id: 'backButtonNavigation',
        buttonText: i18n.t('general.cancel'),
        useButton: true,
        colour: ColoursObj.core.primary,
        action: () => {
            onBackPressed()
        },
    }

    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={headerText}
                navigationBarSubtitle={subHeaderText()}
                navigationBarTrailing={navigationBarTrailing()}
                backButton={backButton}
                backButtonAction={onBackPressed}
                footer={
                    <Stack direction={'row'} justifyContent={'center'}>
                        {!isNewItem(idSubItem) && (
                            <MainButton
                                id={`${id}-delete-button`}
                                onClick={() => {
                                    openDeleteDialog()
                                }}
                                variant="destructiveBorderless"
                            >
                                {deleteButtonText()}
                            </MainButton>
                        )}
                    </Stack>
                }
            >
                <Gutters>
                    <Stack gap={'16px'}>
                        <Stack spacing={'16px'}>
                            {section &&
                                componentsToRender().length > 0 &&
                                componentsToRender().map(
                                    (option: IEditComponent, index: number) => {
                                        return (
                                            <Stack key={index}>
                                                {idSubItem && (
                                                    <ComponentFinderEdit
                                                        _id={idSubItem}
                                                        editComponent={option}
                                                    />
                                                )}
                                            </Stack>
                                        )
                                    }
                                )}

                            <Divider />
                        </Stack>
                    </Stack>
                </Gutters>
            </NavigationView>
        </>
    )
}
